import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, apiRequest } from "@/lib/api";
import { cn } from "@/lib/utils";

import type { Assistant, AssistantPage } from "./types";
import { CharacterAvatar } from "./CharacterAvatar";

export function AssistantSwitcher() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const assistants = useQuery({
    queryKey: ["assistants"],
    queryFn: async () => {
      const response = await api.get<AssistantPage>("/v1/assistants");
      if (!response.ok) throw new Error(response.error);
      return response.data;
    },
  });
  const activeAssistant = assistants.data?.items.find((assistant) => assistant.isActive);

  const activate = useMutation({
    mutationFn: async (assistant: Assistant) => {
      const response = await apiRequest<Assistant>(
        `/v1/assistants/${assistant.id}/activate`,
        { method: "POST", body: "{}" }
      );
      if (!response.ok) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (active) => {
      qc.setQueryData(["assistant"], active);
      qc.invalidateQueries({ queryKey: ["assistants"] });
      qc.invalidateQueries({ queryKey: ["memory"] });
      setOpen(false);
      toast.success(`Переключились на «${active.name}»`, {
        description: "Открыта его отдельная история и память.",
      });
    },
    onError: (error: Error) =>
      toast.error("Не удалось переключить персонажа", { description: error.message }),
  });

  const create = useMutation({
    mutationFn: async () => {
      const response = await apiRequest<Assistant>("/v1/assistants", {
        method: "POST",
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!response.ok) throw new Error(response.error);
      return response.data;
    },
    onSuccess: async (created) => {
      setName("");
      await qc.invalidateQueries({ queryKey: ["assistants"] });
      activate.mutate(created);
    },
    onError: (error: Error) =>
      toast.error("Не удалось создать персонажа", { description: error.message }),
  });

  const busy = activate.isPending || create.isPending;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="min-w-0 gap-2 rounded-xl bg-card/70"
          aria-label={`Персонажи. Сейчас: ${activeAssistant?.name ?? "загрузка"}`}
        >
          {assistants.isLoading ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          ) : (
            <CharacterAvatar
              avatarKey={activeAssistant?.personalityConfig.appearance.avatarKey ?? "companion"}
              accentColor={activeAssistant?.personalityConfig.appearance.accentColor ?? "#BEF241"}
              customAvatarUrl={activeAssistant?.avatarUrl}
              className="h-6 w-6 rounded-lg shadow-none"
            />
          )}
          <span className="hidden max-w-36 truncate sm:inline">
            {activeAssistant?.name ?? "Персонажи"}
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[86dvh] max-w-lg overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle>Ваши персонажи</DialogTitle>
          <DialogDescription>
            У каждого своя память и история. Переключение действует и в web, и в Telegram.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {assistants.isLoading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Загружаю
            </div>
          )}
          {assistants.isError && (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              Не удалось загрузить персонажей.
            </div>
          )}
          {assistants.data?.items.map((assistant) => (
            <button
              key={assistant.id}
              type="button"
              disabled={busy || assistant.isActive}
              onClick={() => activate.mutate(assistant)}
              className={cn(
                "flex w-full items-center gap-3 rounded-2xl border bg-card p-3 text-left transition hover:border-primary/40",
                assistant.isActive && "border-primary/40 bg-accent/50 ring-2 ring-primary/10"
              )}
            >
              <CharacterAvatar
                avatarKey={assistant.personalityConfig.appearance.avatarKey}
                accentColor={assistant.personalityConfig.appearance.accentColor}
                customAvatarUrl={assistant.avatarUrl}
                className="h-10 w-10 rounded-2xl shadow-sm"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold">{assistant.name}</span>
                <span className="text-xs text-muted-foreground">
                  {assistant.isDefault ? "Персонаж по умолчанию · защищён" : "Изолированная память"}
                </span>
              </span>
              {assistant.isActive && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}
        </div>

        <DialogFooter className="border-t pt-4 sm:justify-between">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={80}
            placeholder="Имя нового персонажа"
            onKeyDown={(event) => {
              if (event.key === "Enter" && name.trim() && !busy) create.mutate();
            }}
          />
          <Button
            type="button"
            className="shrink-0 gap-2"
            disabled={!name.trim() || busy}
            onClick={() => create.mutate()}
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
